
import {
  Component,
  ElementRef,
  OnInit,
  WritableSignal,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  output,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { OrderDetailsButtonGroupComponent } from '../../shared/order-details-button-group/order-details-button-group.component';
import { FormsModule } from '@angular/forms';
import { IconComponent } from '../../shared/icons/icon.component';
import { IconNameType } from '../../shared/icons/icon-name-type.enum';
import { TemplateMarkerDirective } from '../../shared/directives/template-marker.directive';
import { ConfigService } from '../services/config.service';
import { OrderType, OrderTypeLookup } from '../models/order-type.enum';
import { StoreStatusService } from '../services/store-status.service';
import { DeferChoice } from '../models/defer-choice.enum';
import { OrderService } from '../services/order.service';
import { IconedSelectionButtonComponent } from '../../shared/iconed-selection-button/iconed-selection-button.component';
import { Order } from '../models/ordering/order.model';
import { AddressFormComponent } from '../../shared/address-form/address-form.component';
import {
  Observable,
  Subject,
  Subscription,
  catchError,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { Address } from '../models/core/address.model';
import { AuthService } from '../services/auth.service';
import { AccountAddressesService } from '../services/account/account-addresses.service';
import { AccountAddressComponent } from '../../shared/account-address/account-address.component';
import { AddressService } from '../services/address.service';
import { AddressValidationResult } from '../models/core/address-validation-result.model';
import { GenericObject } from '../models/core/generic-object';
import { ErrorsService } from '../services/errors.service';
import { MenuService } from '../services/menu.service';
import { OutOfDeliveryWarningComponent } from '../../shared/out-of-delivery-warning/out-of-delivery-warning.component';
import { TempAddressComponent } from '../../shared/temp-address/temp-address.component';
import { AccountCouponsService, PendingApplicationCoupon } from '../services/account/account-coupons.service';
import { InitParamsService } from '../services/init-params.service';
import { DeferService } from '../services/ordering/defer.service';
import { DateTime } from 'luxon';
import { OrderTypeConfig } from '../models/order-type-config.model';
import { ParsedErrorResponse } from '../models/parsed-error-response.model';
import { ParseErrorsMessageComponent } from '../../shared/parse-errors-message/parse-errors-message.component';
import { TranslocoDirective, TranslocoService } from '@ngneat/transloco';
import { ModalName, ModalService } from '../services/modal.service';
import { StoreStatus } from '../models/store-status.model';
import { DottedLabelsComponent } from '../../shared/dotted-labels/dotted-labels.component';
import { Account } from '../models/account/account.model';
import { MerchantStore } from '../models/merchant-store.model';
import { TimeSelectComponent } from '../../shared/ordering/time-select/time-select.component';
import { AuditService } from '../services/audit.service';
import { InitParamsHandlerService } from '../services/init-params-handler.service';

@Component({
  selector: 'cwo-order-creation-form',
  standalone: true,
  imports: [
    OrderDetailsButtonGroupComponent,
    IconedSelectionButtonComponent,
    CommonModule,
    IconComponent,
    AddressFormComponent,
    FormsModule,
    TemplateMarkerDirective,
    AccountAddressComponent,
    OutOfDeliveryWarningComponent,
    TempAddressComponent,
    ParseErrorsMessageComponent,
    TranslocoDirective,
    DottedLabelsComponent,
    TimeSelectComponent,
  ],
  templateUrl: './order-creation-form.component.html',
  styleUrl: './order-creation-form.component.scss',
})
export class OrderCreationFormComponent implements OnInit {
  modalService = inject(ModalService);
  configService = inject(ConfigService);
  storeStatusService = inject(StoreStatusService);
  orderService = inject(OrderService);
  menuService = inject(MenuService);
  authService = inject(AuthService);
  addressService = inject(AddressService);
  accountAddressesService = inject(AccountAddressesService);
  errorsService = inject(ErrorsService);
  accountCouponsService = inject(AccountCouponsService);
  initParamsService = inject(InitParamsService);
  initParamsHandler = inject(InitParamsHandlerService);
  deferService = inject(DeferService);
  transolocoService = inject(TranslocoService);
  private auditService = inject(AuditService);

  isInCheckout = input(false);
  storeStatusValue = input<StoreStatus>();

  accountAddresses = computed(() => this.accountAddressesService.addresses());
  savedAccountAddress = computed(() =>
    this.accountAddressesService.savedAccountAddress()
  );
  store = computed(() => this.configService.configState().store);
  storeStatus = signal<StoreStatus | undefined>(undefined);
  storeStatusAny = signal<StoreStatus | undefined>(undefined);
  useAddressFromAddressBook = () =>
    this.modalService.stateForModal(ModalName.OrderDetails)
      ?.useAddressBookAddress;

  activeOrderType: WritableSignal<OrderTypeConfig | undefined> =
    signal(undefined);
  orderType = OrderType;
  schedule: WritableSignal<string> = signal('');
  formattedTime = '';
  deferDate: WritableSignal<DateTime | undefined> = signal(undefined);
  iconName = IconNameType;
  isAddressFormValid: boolean = false;
  savedTempAddress = false;

  orderTypeChanged = false;
  scheduleChanged = false;
  addressFormChanged = false;

  configState = this.configService.configState;
  deferChoices: string[] = [];
  orderTimeInfo = computed(() => this.orderService.orderTimeInfo());
  order = computed(() => this.orderService.order());
  showNewAddressForm = false;

  address: WritableSignal<Address | undefined> = signal(undefined);
  addressInfo = computed(() => {
    return this.address()
      ? `${this.address()!.street}, ${
          this.address()!.city
        }, ${this.address()!.state?.toUpperCase()} ${this.address()!.zip}`
      : undefined;
  });
  account = computed(() => { return this.authService.state().account; });
  
  addressSetInOrder = computed(() => { 
    return this.order()?.customer?.address?.street !== undefined ? this.order()?.customer?.address : undefined 
  });

  selectedAddress: WritableSignal<Address | undefined> = signal(undefined);
  validationResponse?: AddressValidationResult;
  serverErrors: GenericObject<string[]> | undefined;
  highlightOverrideMessage = false;
  timeSlotsDescriptionText?: string;
  loadingSubscription: Subscription | null = null;
  orderTypeConfigsLoading = false;
  storeClosedForSelectedDate = false;
  timeSlots = signal<string[]>([]);
  orderTypeAriaLabels = computed(() =>
    this.configService
      .configState()
      .orderTypes.map((type) => type.displayName ?? type.type ?? '')
  );

  shouldShowMakeFavoriteStore: boolean = false;
  currentStore: MerchantStore | undefined = undefined;
  displaySuccessCheck: boolean = false;

  showOrderTypeError = false;
  parsedErrors?: ParsedErrorResponse;
  dateTimeErrorsVisible = false;

  embedded = signal(false);

  readonly addressFormComponent = viewChild<AddressFormComponent>('addressFormComponent');
  readonly scheduleHeaderTitle = viewChild<ElementRef<HTMLElement>>('scheduleHeaderTitle');

  readonly successfullyCreated = output();

  readonly editAddress = output<{
    address?: Address;
    isSaved: boolean;
    orderType?: OrderTypeConfig;
}>();

  readonly storeStatusChanged = output<StoreStatus>();

  readonly emitSelectedAddress = output<Address>();

  constructor() {
    this.embedded.set(this.initParamsService.isEmbedded);
    if (
      this.store()?.hasStoreChooser &&
      !this.isInCheckout() &&
      !this.embedded()
    ) {
      this.configService.fetchMerchantStores().subscribe((s) => {
        this.currentStore = s.find((store) => store.isCurrentStore);
        this.shouldShowMakeFavoriteStore = !!(
          this.account() &&
          this.account()?.isInstantiated &&
          this.account()?.favoriteStore !== this.currentStore?.primaryKey
        );
      });
    }

    effect(() => {
      if (this.selectedAddress()) {
        untracked(() => this.emitSelectedAddress.emit(this.selectedAddress()!));
      }
    })

    effect(() => {
      const isLoggedIn = this.authService.state().isLoggedIn;
      const isDelivery = this.activeOrderType()?.type === OrderType.Delivery;
      if (isLoggedIn && isDelivery) {
        untracked(() => this.accountAddressesService.fetchAddresses().subscribe());
      }
    });
  }

  ngOnInit(): void {
    if(this.configService.configState().orderTypeValues.length === 1) {
      this.onOrderTypeChange(this.configService.configState().orderTypeValues[0] as string);
    }
    if (this.useAddressFromAddressBook() !== undefined) {
      this.onOrderTypeChange(
        OrderType.Delivery,
        true,
        !!this.useAddressFromAddressBook()
      );
    } else if (!this.orderService.order().dummy) {
      this.onOrderTypeChange(this.orderService.order().orderType ?? '', true);
      this.address.set(this.addressSetInOrder());
      const convertedObj = this.orderService.getFormattedDateStatus(
        this.orderService.order().deferTime ?? ''
      );
      if (this.orderService.order().deferTime) {
        this.schedule.set(convertedObj.dateStatus);
        this.deferDate?.set(convertedObj.date);
        this.formattedTime = convertedObj.formattedTime;
      } else {
        this.schedule.set(DeferChoice.Asap);
      }
    }
    this.storeStatusService.fetchStoreStatus(OrderType.Any , true).subscribe((status) => {
      this.storeStatusAny.set(status);
    }); 
  }
  getIconName(type: OrderType | undefined): IconNameType {
    switch (type) {
      case 'Pickup':
        return this.iconName.pickup;
      case 'Delivery':
        return this.iconName.delivery;
      case 'Curbside':
        return this.iconName.curbside;
      default:
        return this.iconName.pickup;
    }
  }
  onOrderTypeChange(
    value: string,
    isFromInit = false,
    openingFromAddressBook = false
  ) {
    if (isFromInit) {
      this.orderTypeChanged = false;
    } else {
      this.orderTypeChanged = true;
    }
    this.showNewAddressForm = false;
    const orderType = OrderTypeLookup[value as keyof typeof OrderTypeLookup];
    const active = this.configService
      .configState()
      .orderTypes.find((orderTypeConf) => orderTypeConf.type === orderType);
    this.activeOrderType.set(active);

    if (this.orderTypeChanged)
      this.auditService.createAudit(() => `Setting order type to ${value}`);

    if (active) this.showOrderTypeError = false;
    this.orderTypeConfigsLoading = true;
    this.storeStatusService
      .fetchStoreStatus(orderType, true)
      .pipe(
        mergeMap((status) => {
          this.storeStatus.set(status);
          this.storeStatusChanged.emit(status);
          if (!status?.isOpen) {
            return of(status);
          }

          const isInitialized = this.order()?.isInitialized;
          const needsAddress = this.activeOrderType()?.needsAddress;

          if (!isInitialized) {
            return this.orderService
              .fetchAsapNote(orderType)
              .pipe(map(() => status));
          }

          let streetName = '';
          let zip = '';

          if (needsAddress) {
            streetName = this.addressSetInOrder()?.street ?? '';
            zip = this.addressSetInOrder()?.zip ?? '';
          }

          return this.orderService
            .fetchAsapNote(orderType, streetName, zip, true)
            .pipe(map(() => status));
        }),
        mergeMap((status) =>
          this.deferService.getCalendarEvents(orderType).pipe(map(() => status))
        )
      )
      .subscribe((status) => {
         const noTimeSlotsToday = this.hasNoTimeSlotsToday(status);
        this.deferChoices = this.storeStatusService.calculateDeferChoices(
          this.activeOrderType()?.type,
          status
        );
          if (noTimeSlotsToday) {
          this.deferChoices = [DeferChoice.Future];
        }
        if (!this.deferChoices.includes(this.schedule())) {
          this.schedule.set(this.deferChoices[0]);
          this.auditService.createAudit( () => `Defer type was set to ${this.schedule()}` );

          if (
            this.schedule() === DeferChoice.Future &&
            status.deferredDayTimes &&
            Object.keys(status.deferredDayTimes).length > 0
          ) {
            this.deferDate?.set(
              DateTime.fromISO(Object.keys(status.deferredDayTimes)[0], {
                setZone: false,
              })
            );
            this.resetDateTimeErrors();
          }
        }
        this.orderTypeConfigsLoading = false;
        if (!isFromInit) {
          setTimeout(() => this.scheduleHeaderTitle()?.nativeElement.focus());
        }
      });

    if (value === OrderType.Delivery && this.authService.state().isLoggedIn) {
      this.accountAddressesService.fetchAddresses().subscribe((addresses) => {
        if (addresses.length) {
          if (openingFromAddressBook) {
            this.selectedAddress.set(this.useAddressFromAddressBook());
          } 
          else if (this.addressSetInOrder()?.name) {
            const existingAddress = addresses.find(
              (add) => add.name === this.addressSetInOrder()?.name
            );
            this.selectedAddress.set(existingAddress);
          }
          else if (this.addressSetInOrder() && !this.addressSetInOrder()?.name) {
            const matching = this.findMatchingAddress(addresses, this.addressSetInOrder()!);
              
            const existingAddress = addresses.find(
              (add) => add.name === matching?.name
            );
            this.selectedAddress.set(existingAddress);
          }
          else if (this.address()) {
            const matching = this.findMatchingAddress(addresses, this.address()!);
            if (matching) {
              const existingAddress = addresses.find(
                (add) => add.name === matching?.name
              );
              this.selectedAddress.set(existingAddress);
            } else {
              this.selectedAddress.set(undefined);
            }
          }
          else {
            this.selectedAddress?.set(this.savedAccountAddress() || addresses[0]);
          }

          if (this.selectedAddress() !== undefined) {
            this.auditService.createAudit(
              () =>
                `Address ${this.selectedAddress() ? this.selectedAddress()?.street : ''}, ${
                  this.selectedAddress()?.city
                }, ${this.selectedAddress()?.state?.toUpperCase()} ${
                  this.selectedAddress()?.zip
                } selected`
            );
          } else if (this.address()) {
            this.auditService.createAudit(
              () =>
                `Address ${this.address()?.street}, ${
                  this.address()?.city
                }, ${this.address()?.state?.toUpperCase()} ${
                  this.address()?.zip
                } selected`
            );
          }
        }
      });
    }
  }

  findMatchingAddress(addresses: Address[], targetAddress:Address) {
    
    const targetStreet = targetAddress?.street?.toUpperCase();
    
    // First find addresses with matching street
    const sameStreetAddresses = addresses.filter(address => 
        address?.street?.toUpperCase() === targetStreet
    );
    // If no addresses with same street, return null
    if (sameStreetAddresses.length === 0) return null;
    if (sameStreetAddresses.length === 1) {
        return this.areAddressesEqual(sameStreetAddresses[0], targetAddress) 
            ? sameStreetAddresses[0] 
            : null;
    }
    
    // Multiple addresses with same street - find first where all fields match
    return sameStreetAddresses.find(address => 
        this.areAddressesEqual(address, targetAddress)
    );
}

areAddressesEqual(addr1:Address, addr2:Address) {
    // Check required fields
    if (addr1.streetNum !== addr2.streetNum) return false;
    if (addr1.zip !== addr2.zip) return false;
    if (addr1.addressType?.toUpperCase() !== addr2.addressType?.toUpperCase()) return false;
    if (addr1.city?.toUpperCase() !== addr2.city?.toUpperCase()) return false;
    if (addr1.state?.toUpperCase() !== addr2.state?.toUpperCase()) return false;
    
    // Check diff/value fields that exist in target address
    const fields = ['diff1', 'value1', 'diff2', 'value2', 'diff3', 'value3'];
    
    for (const field of fields) {
        if (field in addr2) {
            const val1 = addr1[field] ? addr1[field].toString().toUpperCase().trim() : '';
            const val2 = addr2[field] ? addr2[field].toString().toUpperCase().trim() : '';
            if (val1 !== val2) return false;
        }
    }
    
    return true;
}

  onTimeChoiceChanged(value: string) {
    this.timeSlotsDescriptionText = undefined;
    this.scheduleChanged = true;
    this.storeClosedForSelectedDate = false;
    this.serverErrors = undefined;
    this.validationResponse = undefined;
    if (value === 'Today' || value === 'Future') {
      this.deferDate.set(value === 'Today' ? DateTime.now() : undefined);
      this.resetDateTimeErrors();
    }
    this.schedule.set(value);
    this.auditService.createAudit(() => `Setting defer type to ${value}`);
  }

  handleDefferDateUpdates(date: DateTime) {
    if (
      this.orderService.order().deferTime &&
      date.equals(DateTime.fromISO(this.orderService.order().deferTime!))
    ) {
      this.scheduleChanged = false;
    } else {
      this.scheduleChanged = true;
      this.auditService.createAudit(
        () => `Setting defer time to ${date.toFormat('yyyy-MM-dd HH:mm')}`
      );
    }
    this.deferDate?.set(date);
    this.resetDateTimeErrors();
    this.serverErrors = undefined;
    this.validationResponse = undefined;
  }

  handleAddressFormValidation(isFormValid: boolean) {
    this.isAddressFormValid = isFormValid;
  }

  getAsapLabel(choice: string) {
    if (choice === DeferChoice.Asap) {
      const tempActive = this.configState().orderTypes.find(
        (type) => type.type === this.activeOrderType()?.type
      );
      return tempActive?.asapLabel ?? DeferChoice.Asap;
    } else {
      return choice;
    }
  }

  deferChoiceAriaLabels() {
    return this.deferChoices.map((choice) => this.getAsapLabel(choice));
  }

  onSubmit(overrideAddress?: boolean): Observable<boolean> | undefined {
    this.serverErrors = undefined;
    this.parsedErrors = undefined;
    if (
      !this.orderService.order().dummy &&
      !(
        this.orderTypeChanged ||
        this.scheduleChanged ||
        this.addressFormChanged
      )
    ) {
      this.successfullyCreated.emit();
      return undefined;
    }
    if (this.storeClosedForSelectedDate) {
      this.parsedErrors = {
        allErrors: [
          this.transolocoService
            .translate(
              'shared_components.calendar_component.closed_store_message'
            )
            .slice(0, -1),
        ],
      };
      this.dateTimeErrorsVisible = true;
      return undefined;
    }

    const completed = new Subject<boolean>();
    if (
      this.activeOrderType()?.type === OrderType.Delivery &&
      this.validationResponse &&
      !overrideAddress
    ) {
      this.highlightOverrideMessage = !this.highlightOverrideMessage;
      this.addressFormComponent()?.toggleHighlightOverrideMessage();
      return;
    }
    this.validationResponse = undefined;

    const orderType = this.activeOrderType()?.type;

    if (!orderType) {
      this.auditService.createAudit(() => 'Order Type has not been selected');
      this.showOrderTypeError = true;
      return;
    }
    setTimeout(() => {
      if (!this.isFormValid()) {
        completed.next(false);
        completed.complete();
        return;
      }

      const isInitialized =
        !!this.orderService.order().orderType &&
        this.orderService.order().orderType !== OrderType.Any;

      const order = !isInitialized
        ? new Order()
        : this.orderService.order().copy();

      order.orderType = orderType;
      if (
        (this.schedule() === DeferChoice.Future ||
          this.schedule() === DeferChoice.Today) &&
        this.deferDate()
      ) {
        order.deferTime = this.deferDate()!.toISO() ?? '';
      } else {
        order.deferTime = null;
      }

      //this should be enhancced later on with params checking
      order.isMobileSource = false;

      if (this.activeOrderType()?.type === OrderType.Delivery) {
        let newSelectedAddress;

        if (this.selectedAddress()) {
          newSelectedAddress =
            this.accountAddresses().find(
              (addr) => addr.name === this.selectedAddress()?.name
            ) || this.selectedAddress();
        } else if (this.accountAddresses().length && !this.showNewAddressForm) {
          newSelectedAddress = this.address();
        } else {
          newSelectedAddress = this.addressFormComponent()?.addressForm?.value;
        }

        const address = new Address().deserialize(newSelectedAddress);

        if (!this.authService.state().isLoggedIn) {
          address.name = 'home';
        }
        //trimming address name since server allows multiple values with the same name to be added when we have a space in the end
        address.name = address.name?.trim();

        if (overrideAddress) {
          this.loadingSubscription = this.overrideAddressAndProceed(
            address,
            !isInitialized,
            order
          )
            .pipe(
              switchMap(() => {
                return this.menuService.fetchPanels(
                  this.activeOrderType()?.type, this.order().deferTime
                );
              })
            )
            .subscribe({
              complete: () => {
                completed.next(true);
                completed.complete();
                this.loadingSubscription = null;
                this.successfullyCreated.emit();
              },
            });
        } else {
          const checkDeferTimeSubscription =
            !order.deferTime && this.address()
              ? this.orderService.updateOrder(order).pipe(map(() => null))
              : of(null);
          this.loadingSubscription = checkDeferTimeSubscription
            .pipe(
              switchMap(() =>
                this.validateAddressAndProceed(address, !isInitialized, order)
              ),
              switchMap((validationResponse) => {
                return validationResponse?.error
                  ? of(validationResponse)
                  : this.menuService
                      .fetchPanels(this.activeOrderType()?.type , this.order().deferTime)
                      .pipe(map(() => validationResponse));
              })
            )
            .subscribe({
              next: (validationResponse) => {
                completed.next(true);
                completed.complete();
                this.loadingSubscription = null;
                if (validationResponse?.error) {
                  this.addressFormComponent()?.enableErrors();
                  this.validationResponse = validationResponse;
                  this.auditService.createAudit(
                    () =>
                      `Address confirmation warning tooltip opened with the following data context: ${validationResponse.error}`
                  );
                } else {
                  if (
                    this.addressFormComponent()?.addressForm?.controls[
                      'saveToAddressBook'
                    ].value
                  )
                    this.auditService.createAudit(
                      () =>
                        `New address has been created: {addressName: ${
                          address.name
                        }, fullAddress: ${address.street}, ${address.city}, ${
                          address.state
                        } ${address.zip}, ${
                          address.diff1 && `${address.diff1}: ${address.value1}`
                        }${
                          address.diff2
                            ? `, ${address.diff2}: ${address.value2}`
                            : ''
                        }}`
                    );

                  this.auditService.createAudit(
                    () =>
                      `Address: ${address.street}, ${address.city}, ${address.state} ${address.zip} selected`
                  );
                  this.auditService.createAudit(
                    () =>
                      `Setting order type to ${orderType} at ${
                        this.schedule() === DeferChoice.Asap
                          ? 'ASAP'
                          : this.deferDate()?.toFormat('yyyy-MM-dd HH:mm')
                      }`
                  );
                  this.successfullyCreated.emit();
                  this.menuService.refreshMenu();
                }
              },
              error: (error) => {
                completed.next(false);
                completed.complete();
                this.loadingSubscription = null;
                const parsedError =
                  this.errorsService.parseErrorResponse(error);
                if (
                  parsedError.errors?.name ||
                  parsedError.errors?.street ||
                  parsedError.errors?.zip
                ) {
                  this.serverErrors = parsedError.errors;
                  const auditText = Object.values(parsedError.errors)
                    .flat()
                    .join(', ');
                  this.auditService.createAudit(
                    () => `Order Details Errors: ${auditText}`
                  );
                } else {
                  this.parsedErrors = parsedError;
                  if (error?.error?.errors) {
                    const auditText = Object.values(error.error.errors)
                      .flat()
                      .join(', ');
                    this.auditService.createAudit(
                      () => `Order Details Errors: ${auditText}`
                    );
                  }
                }
              },
            });
        }
      } else {
        // Handle non-Delivery order types
        const orderOperation = !isInitialized
          ? this.orderService.createOrder(order)
          : this.orderService.updateOrder(order);

        this.loadingSubscription = orderOperation
            .pipe(
              tap(() => {
                if (this.accountCouponsService.unsubmittedCoupon()) {
                  if (this.initParamsHandler.shouldTriggerSpecialWizard()) {
                    this.initParamsHandler.handleSpecialQueryParams();
                    return;
                  }

                  this.orderService
                    .addCoupon(
                      {
                      code: this.accountCouponsService.unsubmittedCoupon()
                        ?.code,
                      type: this.accountCouponsService.unsubmittedCoupon()
                        ?.type,
                    },
                    this.accountCouponsService.unsubmittedCoupon()
                      ?.triggeringPoint,
                    this.accountCouponsService.unsubmittedCoupon()
                      ?.triggeringArea
                  )
                  .pipe(
                    switchMap(() => this.orderService.updateOrder(order)),
                    catchError((error) => {
                      if (error.error?.errorType === 'APPLICATION_FAILED' || error.error?.errorType === 'NOT FOUND') {
                        const unsubmitted = this.accountCouponsService.unsubmittedCoupon();
                        if (unsubmitted?.code) {
                          const pending: PendingApplicationCoupon = {
                            code: unsubmitted.code,
                            type: unsubmitted.type,
                            triggeringPoint: unsubmitted.triggeringPoint,
                            triggeringArea: unsubmitted.triggeringArea,
                          };
                          this.accountCouponsService.addPendingApplicationCoupon(pending);
                        }
                      }
                      setTimeout(() => {
                        this.modalService.openGenericModal({
                          modalTitle: 'Issue with coupon',
                          modalMessage: error.error.error,
                          isAlert: true,
                        });
                      }, 1000);
                      return of(undefined);
                    })
                  )
                  .subscribe(() =>
                    this.initParamsService.clearParam('addCoupon')
                  );
              }
            }),
            switchMap(() =>
              this.menuService.fetchPanels(this.activeOrderType()?.type , this.order().deferTime)
            )
          )
          .subscribe({
            next: () => {
              completed.next(true);
              completed.complete();
              this.accountCouponsService.removeUnsubmittedCoupon();
              this.loadingSubscription = null;
              this.successfullyCreated.emit();
              this.menuService.refreshMenu();
            },
            error: (err) => {
              completed.next(false);
              completed.complete();
              this.loadingSubscription = null;
              this.parsedErrors = this.errorsService.parseErrorResponse(err);
            },
          });
      }
    }, 0);

    return completed.asObservable();
  }

  validateAddressAndProceed = (
    address: Address,
    isCreating: boolean,
    order: Order
  ) => {
    this.auditService.createAudit(
      () =>
        `Validating address with the following data: ${JSON.stringify(address)}`
    );
    const orderAddressValidation$ =
      this.authService.state().isLoggedIn && this.showNewAddressForm
        ? this.addressService.validateNewAddress(address).pipe(
            switchMap((validationResponse) => {
              if (validationResponse?.error) {
                return of(validationResponse);
              }
              return this.orderService.validateAddress(
                address,
                order?.deferTime ?? DateTime.now().toISO()
              );
            })
          )
        : this.orderService.validateAddress(
            address,
            order?.deferTime ?? DateTime.now().toISO()
          );

    return orderAddressValidation$.pipe(
      switchMap((validationResponse) => {
        // Check if validationResponse contains errors
        if (validationResponse?.error) {
          return of(validationResponse); // Return validationResponse to handle it later
        }

        if (
          this.addressFormComponent()?.addressForm?.controls['saveToAddressBook']
            .value
        ) {
          return this.accountAddressesService.addAddress(address).pipe(
            map(() => validationResponse) // Continue passing validation response
          );
        } else {
          return of(validationResponse); // Pass the validation response along
        }
      }),
      switchMap((validationResponse) => {
        if (validationResponse?.error) {
          // Return observable that completes without further processing
          return of(validationResponse);
        }

        if (isCreating) {
          return this.orderService.createOrder(order).pipe(
            switchMap(() => {
              return this.orderService.updateOrderCustomerAddress(address).pipe(
                map(() => validationResponse) // Continue passing validation response
              );
            })
          );
        } else {
          return this.orderService.updateOrder(order).pipe(
            switchMap(() => {
              return this.orderService.updateOrderCustomerAddress(address).pipe(
                map(() => validationResponse) // Continue passing validation response
              );
            })
          );
        }
      })
    );
  };

  overrideAddressAndProceed = (
    address: Address,
    isCreating: boolean,
    order: Order
  ) => {
    const tempAdd = address.copy();
    tempAdd.force = true;
    if (isCreating) {
      return this.orderService.createOrder(order).pipe(
        switchMap(() => {
          return this.orderService.updateOrderCustomerAddress(tempAdd).pipe(
            mergeMap(() => {
              if (
                this.addressFormComponent()?.addressForm?.controls[
                  'saveToAddressBook'
                ].value
              ) {
                return this.accountAddressesService.addAddress(tempAdd);
              } else {
                return of(null);
              }
            })
          );
        })
      );
    } else {
      return this.orderService.updateOrder(order).pipe(
        switchMap(() => {
          return this.orderService.updateOrderCustomerAddress(tempAdd).pipe(
            mergeMap(() => {
              if (
                this.addressFormComponent()?.addressForm?.controls[
                  'saveToAddressBook'
                ].value
              ) {
                return this.accountAddressesService.addAddress(tempAdd);
              } else {
                return of(null);
              }
            })
          );
        })
      );
    }
  };

  resetDateTimeErrors() {
    if (this.dateTimeErrorsVisible) {
      this.parsedErrors = undefined;
    }
    this.dateTimeErrorsVisible = false;
  }

  isFormValid() {
    if (
      (this.activeOrderType()?.type === OrderType.Delivery &&
        (this.showNewAddressForm || !this.accountAddresses().length) &&
        !this.isAddressFormValid &&
        !this.selectedAddress()) ||
      !this.activeOrderType()?.type ||
      !this.schedule()
    ) {
      if (
        this.activeOrderType()?.type === OrderType.Delivery &&
        !this.isAddressFormValid &&
        !this.selectedAddress()
      ) {
        this.addressFormComponent()?.enableErrors();
      }
      return false;
    } else {
      if (
        (this.schedule() === 'Future' || this.schedule() === 'Today') &&
        !this.deferDate()
      ) {
        this.parsedErrors = {
          allErrors: [
            this.schedule() === 'Future'
              ? this.transolocoService.translate(
                  'shared_components.order_creation_form.errors.no_date'
                )
              : this.transolocoService.translate(
                  'shared_components.order_creation_form.errors.no_time'
                ),
          ],
        };
        this.dateTimeErrorsVisible = true;
        return false;
      }
      return true;
    }
  }

  calcDate(toParse: string | undefined) {
    if (toParse != undefined) return new Date(toParse);
    else return new Date();
  }

  savedAddressAction(action: 'select' | 'edit' | 'delete', address: Address) {
    this.serverErrors = undefined;
    this.validationResponse = undefined;
    if (action === 'select') {
      this.auditService.createAudit(
        () =>
          `Address: ${address.street}, ${address.city}, ${address.state} ${address.zip} selected`
      );
      if (address.name !== this.addressSetInOrder()?.name) {
        this.addressFormChanged = true;
      } else {
        this.addressFormChanged = false;
      }
      this.selectedAddress.set(address);
      this.showNewAddressForm = false;
      this.address.set(undefined);
    }
    if (action === 'edit') {
      this.editAddress.emit({
        address: address,
        isSaved: true,
        orderType: this.activeOrderType(),
      });
    }
    if (action === 'delete') {
      this.accountAddressesService
        .deleteAddress(address)
        .pipe(mergeMap(() => this.accountAddressesService.fetchAddresses()))
        .subscribe((addresses) => {
          this.auditService.createAudit(
            () =>
              `Address has been deleted: {addressName: ${
                address.name
              }, fullAddress: ${address.street}, ${address.city}, ${
                address.state
              } ${address.zip}, ${
                address.diff1 && `${address.diff1}: ${address.value1}`
              }${
                address.diff2
                  ? `${
                      address.diff2
                        ? `, ${address.diff2}: ${address.value2}`
                        : ''
                    }`
                  : ''
              }}`
          );
          if (!this.showNewAddressForm) {
            if (addresses.length) {
              if (this.selectedAddress()?.name === address.name) {
                this.selectedAddress.set(addresses[0]);
                this.address.set(undefined);
              }
            } else {
              this.address.set(undefined);
              this.selectedAddress.set(undefined);
              this.showNewAddressForm = true;
            }
            this.addressFormChanged = true;
          }
        });
    }
  }

  toggleNewAddressForm() {
    this.auditService.createAudit(() => 'Add New button clicked');
    this.showNewAddressForm = true;
    this.selectedAddress.set(undefined);
    this.validationResponse = undefined;
  }

  handleOverrideAddress() {
    this.onSubmit(true);
  }

  handleFormChanging() {
    this.addressFormChanged = true;
    this.validationResponse = undefined;
    this.selectedAddress.set(undefined);
    this.serverErrors = undefined;
  }

  handleNoThankyou() {
    this.auditService.createAudit(
      () => 'Cancel button clicked on Change Store window.'
    );
    this.validationResponse = undefined;
  }

  editOrderAddress(isTempAddressSaved = false) {
    this.editAddress.emit({
      address: this.address(),
      isSaved: isTempAddressSaved,
      orderType: this.activeOrderType(),
    });
    if (isTempAddressSaved) {
      this.address.set(undefined);
    }
  }

  updateOrderAddress() {
    this.address.set(this.addressSetInOrder());
  }

  updateAddresses() {
    return this.accountAddressesService.fetchAddresses().pipe(
      tap((addresses) => {
        if (addresses.length) {
          if (this.addressSetInOrder()?.name) {
            const existingAddress = addresses.find(
              (add) =>
                add.name === this.addressSetInOrder()?.name
            );
            this.selectedAddress.set(existingAddress);
          } else {
            this.selectedAddress.set(this.savedAccountAddress() || addresses[0]);
          }
        }
      })
    );
  }

   private hasNoTimeSlotsToday(status?: StoreStatus): boolean {
    if (!status?.nextOpenTime || !status?.closeTime) {
      return false;
    }

    const zone = status.timezone;
    const now = zone ? DateTime.now().setZone(zone) : DateTime.now();
    const nextOpen = zone
      ? DateTime.fromISO(status.nextOpenTime, { zone })
      : DateTime.fromISO(status.nextOpenTime);
    const close = zone
      ? DateTime.fromISO(status.closeTime, { zone })
      : DateTime.fromISO(status.closeTime);

    if (!nextOpen.hasSame(now, 'day') || !close.hasSame(now, 'day')) {
      return false;
    }

    return nextOpen.toMillis() === close.toMillis();
  }

  handleAvailableHours(timeSlots: string[]) {
    this.timeSlots.set(timeSlots);
    if (timeSlots.length) {
      this.timeSlotsDescriptionText = `${DateTime.fromISO(
        timeSlots[0]
      ).toFormat('hh:mm a')} - ${DateTime.fromISO(
        timeSlots[timeSlots.length - 1]
      ).toFormat('hh:mm a')}`;
    } else {
      this.timeSlotsDescriptionText = undefined;
    }
  }

  handleStoreClosedForSelectedDate(storeClosed: boolean) {
    this.storeClosedForSelectedDate = storeClosed;
  }

  handleChangeStore() {
    this.auditService.createAudit(
      () => 'Change Store button clicked on Order Details modal.'
    );
    this.modalService.updateModalState(ModalName.OrderDetails, {
      showOrderEditor: false,
      showStoreChooser: true,
    });
  }

  setFavoriteStore() {
    this.auditService.createAudit(() => 'Favorite store has been updated');
    if (this.currentStore?.primaryKey) {
      this.setMyFavoriteStore(this.currentStore?.primaryKey).subscribe(
        (res) => (this.displaySuccessCheck = !!res)
      );
    }
  }

  private setMyFavoriteStore(favoriteStore: string): Observable<Account> {
    const accountCopy = new Account().deserialize({
      ...this.account(),
      favoriteStore,
    });
    return this.authService.updateFavoriteStore(accountCopy);
  }
}
